import React, { useState, useEffect, useRef } from 'react';
import { YoutubeEmbed } from '../../containers/PageBuilder/Primitives/YoutubeEmbed/YoutubeEmbed';
import css from './AdBanner.module.css';

const IMAGE_SLIDE_DELAY = 4000;
const YOUTUBE_VIDEO_ENDED_EVENT = 'youtubeVideoEnded';

/**
 * AdBanner — auto-advancing image carousel with swipe/drag support.
 *
 * @param {{ images: Array<{ url: string, alt: string, href?: string }> }} props
 */
const AdBanner = ({ images = [], className, aspectRatio }) => {
  const [current, setCurrent] = useState(0);
  const total = images.length;
  const touchStartX = useRef(null);
  const dragStartX = useRef(null);
  const isDragging = useRef(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    if (total <= 1) return;
    const currentItem = images[current];
    if (currentItem?.type === 'youtube') return;

    const timer = setTimeout(() => setCurrent(c => (c + 1) % total), IMAGE_SLIDE_DELAY);
    return () => clearTimeout(timer);
  }, [current, images, total]);

  useEffect(() => {
    if (total <= 1) return;

    const onYoutubeVideoEnded = () => {
      setCurrent(c => (c + 1) % total);
    };
    const banner = bannerRef.current;

    banner?.addEventListener(YOUTUBE_VIDEO_ENDED_EVENT, onYoutubeVideoEnded);
    return () => {
      banner?.removeEventListener(YOUTUBE_VIDEO_ENDED_EVENT, onYoutubeVideoEnded);
    };
  }, [total]);

  if (!total) return null;

  const goNext = () => setCurrent(c => (c + 1) % total);
  const goPrev = () => setCurrent(c => (c - 1 + total) % total);

  // Touch events (mobile)
  const onTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = e => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? goNext() : goPrev();
    touchStartX.current = null;
  };

  // Mouse drag events (desktop)
  const onMouseDown = e => {
    dragStartX.current = e.clientX;
    isDragging.current = false;
  };
  const onMouseMove = e => {
    if (dragStartX.current !== null && Math.abs(e.clientX - dragStartX.current) > 5) {
      isDragging.current = true;
    }
  };
  const onMouseUp = e => {
    if (dragStartX.current === null) return;
    const diff = dragStartX.current - e.clientX;
    if (Math.abs(diff) > 40) diff > 0 ? goNext() : goPrev();
    dragStartX.current = null;
  };

  // Prevent click-through on links when dragging
  const onClickCapture = e => {
    if (isDragging.current) {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = false;
    }
  };

  const renderSlideContent = item => {
    if (item.type === 'youtube' && item.youtubeVideoId) {
      return (
        <YoutubeEmbed
          rootClassName={css.adBannerVideo}
          youtubeVideoId={item.youtubeVideoId}
          aspectRatio={item.aspectRatio || aspectRatio || '16/9'}
          autoplay
        />
      );
    }

    const image = (
      <img src={item.url} alt={item.alt} className={css.adBannerImg} draggable={false} />
    );

    return item.href ? (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={css.adBannerLink}>
        {image}
      </a>
    ) : (
      image
    );
  };

  return (
    <div
      ref={bannerRef}
      className={[css.adBanner, className].filter(Boolean).join(' ')}
      style={aspectRatio ? { aspectRatio } : undefined}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onClickCapture={onClickCapture}
    >
      {images.map((img, i) => (
        <div
          key={i}
          className={css.adBannerSlide}
          style={{
            position: 'absolute',
            inset: 0,
            opacity: i === current ? 1 : 0,
            transition: 'opacity 0.4s ease-in-out',
            pointerEvents: i === current ? 'auto' : 'none',
          }}
        >
          {i === current || img.type !== 'youtube' ? renderSlideContent(img) : null}
        </div>
      ))}
      {total > 1 && (
        <div className={css.adBannerDots}>
          {images.map((_, i) => (
            <button
              key={i}
              className={`${css.adBannerDot} ${i === current ? css.adBannerDotActive : ''}`}
              onClick={() => setCurrent(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdBanner;
